import type { TFunction } from 'react-i18next';
import {
  ERROR_CODES,
  type ErrorCode,
  type ErrorParams,
  type KizenAiUnavailableReason,
  type RestoreFailure,
} from 'ts-components/PiChat/protocol';
import {
  CREDENTIALS_FAILED_REASON,
  type ChatItem,
} from 'ts-components/PiChat/reducer';

export type ClientErrorCode =
  | 'socket_error'
  | 'unknown_snapshot_restored'
  | 'busy_own_turn';

const DEVELOPER_ERROR_CODES = [
  'bad_message',
  'already_started',
  'invalid_context',
  'bad_request',
  'session_create_failed',
  'prompt_failed',
  'abort_failed',
  'unsupported',
] as const satisfies readonly ErrorCode[];
type DeveloperErrorCode = (typeof DEVELOPER_ERROR_CODES)[number];
type UserErrorCode = Exclude<ErrorCode, DeveloperErrorCode>;

export interface ErrorCopy {
  text: string;
  details?: string[];
  code?: string;
}

const isUserErrorCode = (code: string): code is UserErrorCode =>
  (ERROR_CODES as readonly string[]).includes(code) &&
  !(DEVELOPER_ERROR_CODES as readonly string[]).includes(code);

const CODE_TOKEN = /^[a-z0-9_]{1,64}$/;

const generic = (code: string | null, t: TFunction): ErrorCopy =>
  code && CODE_TOKEN.test(code)
    ? { text: genericErrorText(t), code }
    : { text: genericErrorText(t) };

const paramString = (params: unknown, key: string): string | undefined =>
  (params as Record<string, string> | undefined)?.[key];

const genericErrorText = (t: TFunction) =>
  t('Something went wrong. Try again.');

export const modelFailedText = (t: TFunction) =>
  t("The model couldn't respond. Try again.");

export const credentialsErrorText = (t: TFunction) =>
  t('Could not authorize the AI session. Try again.');

const skippedFileDetails = (params: unknown, t: TFunction): string[] => {
  const { files, more } = params as ErrorParams['files_skipped'];
  const reasonLabels = {
    too_large: t('too large'),
    not_utf8: t('not a text file'),
    too_many: t('over the file limit'),
    secret: t('secret file'),
  };
  const lines = files.map(({ path, reason }) =>
    t('{{path}} ({{reason}})', { path, reason: reasonLabels[reason] })
  );
  return more > 0
    ? [...lines, t('and {{count}} more', { count: more })]
    : lines;
};

const restoreFailedText = (params: unknown, t: TFunction): string => {
  switch (paramString(params, 'reason') as RestoreFailure | undefined) {
    case 'no_dashlet':
      return t('There is nothing to restore yet.');
    case 'target_changed':
      return t(
        "That version couldn't be restored because it was changed somewhere else. Reload, then try again."
      );
    case 'target_busy':
      return t(
        "That version couldn't be restored. Make sure it's open with no unsaved changes, then try again."
      );
    case 'unknown_outcome':
      return t(
        "It isn't clear whether that version was restored. Check it before you continue."
      );
    case 'unrestorable':
      return t("That version can't be restored.");
    case 'apply':
      return t("That version couldn't be applied. Try again.");
    default:
      return t("That version couldn't be restored. Try again.");
  }
};

// TODO(copy): placeholder text for every reason; replace with product copy.
const kizenAiUnavailableText = (params: unknown, t: TFunction): string => {
  switch (
    paramString(params, 'reason') as KizenAiUnavailableReason | undefined
  ) {
    case 'permission':
      // TODO(copy): placeholder
      return t('Kizen AI is unavailable: no permission.');
    case 'feature_disabled':
      // TODO(copy): placeholder
      return t('Kizen AI is unavailable: feature disabled.');
    case 'billing':
      // TODO(copy): placeholder
      return t('Kizen AI is unavailable: billing.');
    default:
      // TODO(copy): placeholder
      return t('Kizen AI is unavailable.');
  }
};

// TODO(copy): placeholders for the resource_locked busy refusal, one per builder.
const resourceLockedText = (profile: string | undefined, t: TFunction) => {
  switch (profile) {
    case 'code-step-builder':
      return t(
        "This workflow is being edited in another KiCode session. Try again when it's done."
      );
    case 'block-builder':
      return t(
        "This block is being edited in another KiCode session. Try again when it's done."
      );
    case 'business-app':
      return t(
        "This app is being edited in another KiCode session. Try again when it's done."
      );
    default:
      return t(
        "This item is being edited in another KiCode session. Try again when it's done."
      );
  }
};

const knownErrorText = (
  code: UserErrorCode,
  params: unknown,
  t: TFunction,
  profile?: string
): string => {
  switch (code) {
    case 'not_ready':
      return t(
        "The AI session isn't ready yet. Your message is back in the message box."
      );
    case 'auth':
      switch (paramString(params, 'reason')) {
        case 'expired':
          return t(
            'Your AI session authorization expired. Reconnect to continue.'
          );
        case 'rejected':
          return t(
            'Your AI session authorization was refused. Reload the page, then reconnect.'
          );
        case 'unverified':
          return t(
            'Your AI session authorization could not be checked. Try again in a few minutes.'
          );
        default:
          return t(
            'The AI session could not be authorized. Reconnect to try again.'
          );
      }
    case 'not_found':
      return t('This AI conversation could not be found.');
    case 'forbidden':
      return t("You don't have permission to continue this AI conversation.");
    case 'conflict':
      return t(
        'This conversation was changed in another tab or window. Reconnect to continue.'
      );
    case 'conversation_invalid':
      return t("This AI conversation can't be continued.");
    case 'model_unavailable':
      return t("That model isn't available.");
    case 'busy':
      switch (paramString(params, 'reason')) {
        case 'save_pending':
          return t('The last change is still saving. Try again in a moment.');
        case 'resource_locked':
          return resourceLockedText(profile, t);
        default:
          return t(
            'This conversation is busy in another tab or window. Try again in a moment.'
          );
      }
    case 'restore_busy':
      return t('Wait for the current change to finish, then try again.');
    case 'state_unavailable':
      return t(
        "Your message couldn't be sent. It's back in the message box. Try again."
      );
    case 'closed':
      return t(
        "The session ended before this message ran. It's back in the message box."
      );
    case 'cancelled':
      return t(
        "Stopped before this message ran. It's back in the message box."
      );
    case 'persist_failed':
      return t('The last change may not have been saved.');
    case 'files_skipped':
      return t('Some files were left out of saved versions:');
    case 'unknown_snapshot':
      return t('That version is no longer available.');
    case 'restore_failed':
      return restoreFailedText(params, t);
    case 'restore_note_failed':
      return t(
        'The version was restored, but the AI was not told. Mention it in your next message.'
      );
    case 'kizen_ai_unavailable':
      return kizenAiUnavailableText(params, t);
  }
};

export const errorCopy = (
  code: string,
  params: unknown,
  t: TFunction,
  profile?: string
): ErrorCopy => {
  if (!isUserErrorCode(code)) return generic(code, t);
  const text = knownErrorText(code, params, t, profile);
  if (code === 'files_skipped') {
    const details = skippedFileDetails(params, t);
    return details.length ? { text, details } : { text };
  }
  return { text };
};

export const noticeCopy = (
  item: Extract<ChatItem, { kind: 'notice' }>,
  t: TFunction,
  profile?: string
): ErrorCopy => {
  switch (item.source) {
    case 'client':
      return { text: item.text };
    case 'server':
      return errorCopy(item.code, item.params, t, profile);
    case 'client_error':
      return item.code === 'busy_own_turn'
        ? {
            text: t(
              'Wait for the current reply to finish, then send your message again.'
            ),
          }
        : generic(item.code, t);
    case 'model':
      return { text: modelFailedText(t) };
  }
};

export const closeCopy = (
  reason: string | null,
  params: unknown,
  t: TFunction
): ErrorCopy => {
  switch (reason) {
    case 'conflict':
    case 'not_found':
    case 'forbidden':
    case 'auth':
      return errorCopy(reason, params, t);
    case CREDENTIALS_FAILED_REASON:
      return { text: credentialsErrorText(t) };
    case 'timeout_max':
      return {
        text: t('This session reached its time limit. Reconnect to continue.'),
      };
    case 'timeout_idle':
      return {
        text: t('This session closed after being idle. Reconnect to continue.'),
      };
    case 'server_error':
      return {
        text: t('The AI session hit a server error. Reconnect to try again.'),
      };
    case 'capacity':
      return { text: t('The AI service is at capacity. Try again later.') };
    case 'client':
      return { text: t('Disconnected.') };
    default:
      return generic(reason, t);
  }
};
